import { apiClient } from "../client";

/**
 * Puja booking endpoints (see PUJA_API_EXAMPLES.md at the repo root).
 * All routes live under the /api prefix upstream; the BFF proxy already
 * supplies /api, so paths here start at . Public reads need no auth;
 * booking endpoints ride the session like every other authenticated call.
 *
 * Envelope: { success, message, data, meta? } — note `success`, not the
 * `status` flag the catalog endpoints use. `meta` appears only on paginated
 * lists (pujas, temples, search, my-bookings).
 */

export interface PujaMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface PujaEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
  meta?: PujaMeta;
}

export interface PujaCategory {
  id: number;
  title: string;
  slug: string;
  /** Image URLs from the CMS media library, not icon-font classes. */
  icon: string | null;
  image: string | null;
  short_description: string | null;
  description: string | null;
  sort_order: number;
}

export type PujaScheduleType =
  | "one_time"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "festival";

export interface PujaTempleSummary {
  id: number;
  name: string;
  slug: string;
  location?: string | null;
  state?: string | null;
  country?: string | null;
  image?: string | null;
  is_featured?: boolean;
  is_popular?: boolean;
  is_trending?: boolean;
}

export interface PujaTempleDetail extends PujaTempleSummary {
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  about: string | null;
  history: string | null;
  benefits: string | null;
  gallery: string[];
  pujas: PujaListItem[];
}

/** Row shape shared by /pujas, the is_* flag lists and /search-pujas. */
export interface PujaListItem {
  id: number;
  title: string;
  slug: string;
  subtitle: string | null;
  short_description: string | null;
  image: string | null;
  category?: Pick<PujaCategory, "id" | "title" | "slug">;
  temple?: Pick<PujaTempleSummary, "id" | "name" | "slug">;
  schedule_type: PujaScheduleType;
  festival_name: string | null;
  puja_date: string | null;
  /** Lowest original_price across active packages — display only; the
   * payable amount is packages[].final_price on the detail read. */
  starting_price: number;
  is_featured: boolean;
  is_popular: boolean;
  is_trending: boolean;
  is_recommended: boolean;
}

export interface PujaPackageFeature {
  id: number;
  name: string;
  /** Feather icon token from the admin, e.g. "feather-check-circle". */
  icon: string | null;
}

export interface PujaPackage {
  id: number;
  puja_id: number;
  title: string;
  subtitle: string | null;
  image: string | null;
  max_devotees: number;
  original_price: number;
  discounted_price: number | null;
  /** The actual payable amount. */
  final_price: number;
  offer_percentage: number | null;
  is_most_popular: boolean;
  is_best_value: boolean;
  features: PujaPackageFeature[];
}

export interface PujaFaq {
  id: number;
  question: string;
  answer: string;
}

export interface PujaDetail extends Omit<PujaListItem, "category" | "temple" | "starting_price"> {
  description: string | null;
  benefits: string | null;
  who_should_perform: string | null;
  what_devotees_receive: string | null;
  precautions: string | null;
  instructions: string | null;
  gallery: string[];
  category: PujaCategory;
  temple: PujaTempleSummary;
  start_date: string | null;
  end_date: string | null;
  puja_time: string | null;
  cutoff_time: string | null;
  max_devotees: number | null;
  faqs: PujaFaq[];
  packages: PujaPackage[];
}

export interface PujaListParams {
  category?: number;
  temple?: number;
  schedule_type?: PujaScheduleType;
  search?: string;
  page?: number;
  /** Default 20, max 100. */
  limit?: number;
}

// ── Booking flow ───────────────────────────────────────────────────────────

export interface PujaDevoteePayload {
  is_primary?: boolean;
  name: string;
  gotra?: string;
  gender?: "male" | "female" | "other";
  dob?: string;
  mobile?: string;
  email?: string;
}

/** Either a full address block or { puja_address_id } to reuse a saved one. */
export interface PujaAddressPayload {
  puja_address_id?: number | null;
  name?: string;
  mobile?: string;
  email?: string;
  address?: string;
  landmark?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  is_default?: boolean;
}

export interface CreatePujaBookingPayload {
  puja_id: number;
  puja_package_id: number;
  puja_date: string;
  devotees: PujaDevoteePayload[];
  address: PujaAddressPayload;
}

export interface PujaBookingDevotee extends PujaDevoteePayload {
  id: number;
  relation: string | null;
}

export interface PujaBookingAddress {
  id: number;
  name: string;
  mobile: string;
  email: string | null;
  address: string;
  landmark: string | null;
  city: string;
  state: string;
  country: string;
  pincode: string;
  is_default?: boolean;
}

export interface PujaBooking {
  id: number;
  booking_number: string;
  invoice_number: string | null;
  puja?: { id: number; title: string; slug: string; image: string | null };
  package?: { id: number; title: string; max_devotees: number };
  puja_date: string | null;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  currency: string;
  payment_status: "pending" | "paid" | "failed";
  booking_status: "pending" | "confirmed" | "cancelled" | "completed";
  delivery_status: string | null;
  razorpay_order_id: string | null;
  devotees?: PujaBookingDevotee[];
  address?: PujaBookingAddress;
  created_at: string;
}

export interface PujaRazorpayOrder {
  razorpay_order_id: string;
  /** Paise (Razorpay convention) — 45100 = ₹451.00. */
  amount: number;
  currency: string;
  puja_booking_id: number;
}

export interface VerifyPujaPaymentPayload {
  puja_booking_id: number;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export const puja = {
  /** GET /puja/categories — deity/category tabs. */
  categories: () => apiClient.get<PujaEnvelope<PujaCategory[]>>("/puja/categories"),

  /** GET /pujas — paginated listing with category/temple/search filters. */
  list: (params: PujaListParams = {}) =>
    apiClient.get<PujaEnvelope<PujaListItem[]>>("/pujas", { params: { ...params } }),

  /** GET /pujas/{slug} — full detail incl. packages, faqs, temple. */
  bySlug: (slug: string) => apiClient.get<PujaEnvelope<PujaDetail>>(`/pujas/${slug}`),

  /** Flag lists — not paginated, `limit` only (default 10, max 100). */
  featured: (limit?: number) =>
    apiClient.get<PujaEnvelope<PujaListItem[]>>("/featured-pujas", { params: { limit } }),
  popular: (limit?: number) =>
    apiClient.get<PujaEnvelope<PujaListItem[]>>("/popular-pujas", { params: { limit } }),
  trending: (limit?: number) =>
    apiClient.get<PujaEnvelope<PujaListItem[]>>("/trending-pujas", { params: { limit } }),
  recommended: (limit?: number) =>
    apiClient.get<PujaEnvelope<PujaListItem[]>>("/recommended-pujas", { params: { limit } }),

  /** GET /search-pujas — q required (min 2 chars), paginated. */
  search: (params: { q: string; page?: number; limit?: number }) =>
    apiClient.get<PujaEnvelope<PujaListItem[]>>("/search-pujas", { params: { ...params } }),

  /** GET /temples — paginated; only show_in_listing temples. */
  temples: (params: { state?: string; search?: string; page?: number; limit?: number } = {}) =>
    apiClient.get<PujaEnvelope<PujaTempleSummary[]>>("/temples", { params: { ...params } }),

  /** GET /temples/{slug}. */
  templeBySlug: (slug: string) =>
    apiClient.get<PujaEnvelope<PujaTempleDetail>>(`/temples/${slug}`),

  /** GET /packages/{id}. */
  packageById: (id: number) =>
    apiClient.get<PujaEnvelope<PujaPackage>>(`/packages/${id}`),

  // ── Authenticated booking flow: create-booking → create-order → verify ──

  /** POST /create-booking — pending booking; amount computed server-side. */
  createBooking: (payload: CreatePujaBookingPayload) =>
    apiClient.post<PujaEnvelope<PujaBooking>>("/create-booking", payload),

  /** POST /create-order — Razorpay order for a pending booking. */
  createOrder: (payload: { puja_booking_id: number }) =>
    apiClient.post<PujaEnvelope<PujaRazorpayOrder>>("/create-order", payload),

  /** POST /verify-payment — confirms the booking, generates the invoice. */
  verifyPayment: (payload: VerifyPujaPaymentPayload) =>
    apiClient.post<PujaEnvelope<PujaBooking>>("/verify-payment", payload),

  /** GET /my-bookings — rows omit devotees/address; paginated. */
  myBookings: (params: { page?: number; limit?: number } = {}) =>
    apiClient.get<PujaEnvelope<PujaBooking[]>>("/my-bookings", { params: { ...params } }),

  /** GET /booking-details/{id} — full record, owner-scoped (404 otherwise). */
  bookingDetails: (id: number) =>
    apiClient.get<PujaEnvelope<PujaBooking>>(`/booking-details/${id}`),

  /** POST /cancel-booking — blocked once cancelled/completed. */
  cancelBooking: (payload: { puja_booking_id: number; reason?: string }) =>
    apiClient.post<PujaEnvelope<PujaBooking>>("/cancel-booking", payload),

  /** POST /update-address — creates, or updates when puja_address_id set. */
  updateAddress: (payload: PujaAddressPayload) =>
    apiClient.post<PujaEnvelope<PujaBookingAddress>>("/update-address", payload),

  /** POST /reorder-puja — clones a past booking into a fresh pending one. */
  reorderPuja: (payload: { puja_booking_id: number }) =>
    apiClient.post<PujaEnvelope<PujaBooking>>("/reorder-puja", payload),

  /** GET /download-invoice/{id} streams a PDF — build the href for a link. */
  downloadInvoicePath: (id: number) => `/download-invoice/${id}`,
};
